import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoaderCircle, Columns2, Rows2, Maximize2, RotateCw, Copy, X } from 'lucide-react';
import ContextMenu from '../ui/ContextMenu';
import { useEmbeddedTerminal } from '../../hooks/useEmbeddedTerminal';
import { useSshTerminal } from '../../hooks/useSshTerminal';
import { useTerminalExtStore } from '../../store/terminalExtStore';
import { leafOrder } from './paneTreeModel';
import { registerPaneSink } from './paneSinks';
import { fanOutKeystrokes } from './broadcastInput';
import TerminalPaneHeader from './TerminalPaneHeader';
import PaneExitBar from './PaneExitBar';
import PaneMissingProgram from './PaneMissingProgram';
import PaneMcpChip from './PaneMcpChip';
import { useSplitWithMenu } from './SplitWithMenu';
import TerminalSearchBar from './TerminalSearchBar';
import CopyModeBadge from './CopyModeBadge';
import { usePaneTools } from './usePaneTools';

const STARTING_HINT_MS = 1500;

/** Fan the focused pane's keystrokes out when its tab broadcasts. */
function useBroadcastInput(paneId, tabId) {
  return useCallback((data) => fanOutKeystrokes(paneId, tabId, data), [paneId, tabId]);
}

function LocalPaneBody({ pane, visible, focused, keyFilter, onApi }) {
  const setPaneStatus = useTerminalExtStore((s) => s.setPaneStatus);
  const setPaneTitle = useTerminalExtStore((s) => s.setPaneTitle);
  const setPaneCwd = useTerminalExtStore((s) => s.setPaneCwd);
  const setPaneMcp = useTerminalExtStore((s) => s.setPaneMcp);
  const onInput = useBroadcastInput(pane.id, pane.tabId);
  const { termRef, write, getTerm, getSearch, getPrompts, getLink, setOnCommandDone } = useEmbeddedTerminal({
    visible,
    focused,
    cwd: pane.cwd,
    shellKey: pane.shellKey,
    launch: pane.launch,
    restartNonce: pane.restartNonce,
    onStatus: (status, code, error) => setPaneStatus(pane.id, status, code, error),
    onTitle: (title) => setPaneTitle(pane.id, title),
    onCwd: (cwd) => setPaneCwd(pane.id, cwd),
    onMcp: (mcp) => setPaneMcp(pane.id, mcp),
    onInput,
    keyFilter,
  });
  useEffect(() => registerPaneSink(pane.id, write), [pane.id, write]);
  useEffect(() => onApi({ getTerm, getSearch, getPrompts, getLink, setOnCommandDone }), [onApi, getTerm, getSearch, getPrompts, getLink, setOnCommandDone]);
  return <div ref={termRef} className="terminal-panel__xterm" />;
}

function SshPaneBody({ pane, visible, focused, keyFilter, onApi }) {
  const setPaneStatus = useTerminalExtStore((s) => s.setPaneStatus);
  const onInput = useBroadcastInput(pane.id, pane.tabId);
  const { termRef, connecting, write, getTerm, getSearch, getPrompts, getLink, setOnCommandDone } = useSshTerminal(
    pane.connId,
    true,
    visible,
    (err) => setPaneStatus(pane.id, 'exited', null, err || null),
    // Present only for the AI tab, which adopts a session Rust already opened.
    pane.ai ? { termId: pane.termId, ai: true } : undefined,
    { focused, keyFilter, onInput, onStatus: (status) => setPaneStatus(pane.id, status) }
  );
  useEffect(() => (pane.ai ? undefined : registerPaneSink(pane.id, write)), [pane.id, pane.ai, write]);
  useEffect(() => onApi({ getTerm, getSearch, getPrompts, getLink, setOnCommandDone }), [onApi, getTerm, getSearch, getPrompts, getLink, setOnCommandDone]);
  return (
    <>
      <div ref={termRef} className="terminal-panel__xterm" />
      {connecting && (
        <div className="term-ext-connecting" role="status" aria-label="Connecting">
          <LoaderCircle size={18} className="animate-spin" />
        </div>
      )}
    </>
  );
}

/**
 * One pane: absolutely positioned at its rect, never re-parented (the grid
 * renders every pane as a flat sibling keyed by id, so a layout change only
 * changes `style` and the xterm inside survives). Header and exit bar appear
 * only when they carry information; a single running shell looks exactly
 * like the pre-split terminal.
 */
export default function TerminalPane({ pane, tab, style, visible, focused, multi, zoomHidden, split, keyFilter }) {
  const { t } = useTranslation();
  const st = useTerminalExtStore;
  const [menu, setMenu] = useState(null);
  const [slowStart, setSlowStart] = useState(false);
  const { openSplitWith, splitWithMenu } = useSplitWithMenu();
  const exited = pane.status === 'exited' || pane.status === 'error';
  const name = pane.title || pane.label || t('terminalExt.terminal', 'Terminal');
  // Find bar, copy, links, right-click menu, "command finished" (cards 2–5).
  const tools = usePaneTools(t, { name, visible });
  const { onKey: toolKey } = tools;
  const showHeader = multi || !!pane.profileId || exited;
  const zoomed = tab.zoomedPaneId === pane.id;

  useEffect(() => {
    if (pane.status !== 'starting' || !pane.profileId) return undefined;
    setSlowStart(false);
    const timer = setTimeout(() => setSlowStart(true), STARTING_HINT_MS);
    return () => clearTimeout(timer);
  }, [pane.status, pane.profileId, pane.restartNonce]);

  // Exited pane: Enter restarts, Esc closes, while it is focused.
  const paneKeyFilter = useCallback(
    (e) => {
      if (toolKey(e)) return false;
      if (exited && !pane.ai && e.type === 'keydown' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        if (e.key === 'Enter') {
          st.getState().restartPane(pane.id);
          return false;
        }
        if (e.key === 'Escape') {
          st.getState().closePane(pane.id);
          return false;
        }
      }
      return keyFilter ? keyFilter(e) : true;
    },
    [exited, pane.ai, pane.id, keyFilter, st, toolKey]
  );

  // Buttons and menu open "Split with…" (the shortcuts still duplicate).
  const doSplit = (dir, anchor) => openSplitWith(pane.id, dir, anchor);
  const menuItems = [
    { icon: <Columns2 size={14} />, label: t('terminalExt.splitRightPick', 'Split right…'), onClick: () => doSplit('row', menu), disabled: !split.row.ok },
    { icon: <Rows2 size={14} />, label: t('terminalExt.splitDownPick', 'Split down…'), onClick: () => doSplit('col', menu), disabled: !split.col.ok },
    multi && { icon: <Maximize2 size={14} />, label: zoomed ? t('terminalExt.unzoomPane', 'Restore layout') : t('terminalExt.zoomPane', 'Zoom pane'), onClick: () => st.getState().toggleZoom(tab.id, pane.id) },
    !pane.ai && { icon: <RotateCw size={14} />, label: t('terminalExt.restart', 'Restart'), onClick: () => st.getState().restartPane(pane.id) },
    (pane.liveCwd || pane.cwd) && { icon: <Copy size={14} />, label: t('terminalExt.copyCwd', 'Copy working directory'), onClick: () => navigator.clipboard?.writeText(pane.liveCwd || pane.cwd).catch(() => {}) },
    { icon: <X size={14} />, label: t('terminalExt.closePane', 'Close pane'), onClick: () => st.getState().closePane(pane.id), danger: true },
  ];

  const Body = pane.kind === 'ssh' ? SshPaneBody : LocalPaneBody;
  return (
    <div
      className={`term-pane${focused ? ' term-pane--focused' : ''}${tab.broadcast && !pane.ai ? ' term-pane--broadcast' : ''}${zoomHidden ? ' term-pane--zoom-hidden' : ''}`}
      style={style}
      role="region"
      aria-label={name}
      data-pane-id={pane.id}
      onMouseDownCapture={() => st.getState().focusPane(pane.id)}
      onFocusCapture={() => st.getState().focusPane(pane.id)}
    >
      {showHeader && (
        <TerminalPaneHeader
          pane={pane}
          title={name}
          zoomed={zoomed}
          hiddenCount={zoomed ? leafOrder(tab.layout).length - 1 : 0}
          broadcast={tab.broadcast && !pane.ai}
          splitRow={split.row}
          splitCol={split.col}
          canZoom={multi}
          chips={pane.mcp ? <PaneMcpChip state={pane.mcp} /> : null}
          onSplit={doSplit}
          onZoom={() => st.getState().toggleZoom(tab.id, pane.id)}
          onClose={() => st.getState().closePane(pane.id)}
          onContextMenu={(e) => {
            e.preventDefault();
            setMenu({ x: e.clientX, y: e.clientY });
          }}
        />
      )}
      <div
        className="term-pane-body"
        onContextMenu={(e) => {
          e.preventDefault();
          setMenu({ x: e.clientX, y: e.clientY, body: true, link: tools.hoveredLink() });
        }}
      >
        <Body key={pane.kind === 'ssh' ? `ssh-${pane.restartNonce}` : 'local'} pane={pane} visible={visible} focused={focused} keyFilter={paneKeyFilter} onApi={tools.onApi} />
        {tools.searchOpen && <TerminalSearchBar getSearch={tools.getSearch} getTerm={tools.getTerm} onClose={tools.closeSearch} />}
        {tools.copy.copyMode && <CopyModeBadge onExit={tools.copy.exitCopyMode} />}
        {slowStart && pane.status === 'starting' && (
          <div className="term-pane-starting">{t('terminalExt.startingName', 'Starting {{name}}…', { name })}</div>
        )}
        {pane.status === 'error' && <PaneMissingProgram pane={pane} />}
        {exited && !pane.ai && (
          <PaneExitBar
            name={name}
            code={pane.exitCode}
            error={pane.status === 'error' ? pane.error : null}
            onRestart={() => st.getState().restartPane(pane.id)}
            onOpenShell={pane.kind === 'local' && !pane.shellKey && !pane.launch ? null : () => st.getState().openShellInPane(pane.id)}
            onClose={() => st.getState().closePane(pane.id)}
          />
        )}
      </div>
      {menu && <ContextMenu x={menu.x} y={menu.y} items={menu.body ? [...tools.bodyMenuItems(menu.link), ...menuItems] : menuItems} keyboard={!!menu.body} label={name} onClose={() => setMenu(null)} />}
      {splitWithMenu}
    </div>
  );
}
